import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.addEventListener('click', this.#handleColourBoundaryNavigation, { capture: true, signal });

    const boundaryTarget = this.closest('.shopify-section');
    const requestedBoundary = boundaryTarget?.getAttribute('data-sportstuff-gallery-boundary');
    if (requestedBoundary === 'first' || requestedBoundary === 'last') {
      boundaryTarget.removeAttribute('data-sportstuff-gallery-boundary');
      requestAnimationFrame(() => {
        const requestedIndex = requestedBoundary === 'last' ? (this.slideshow?.slides?.length || 1) - 1 : 0;
        this.slideshow?.select(requestedIndex, undefined, { animate: false });
      });
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * When a colour gallery reaches its boundary, continue with the adjacent
   * colour and let the normal variant-update pipeline render its gallery.
   * @param {MouseEvent} event
   */
  #handleColourBoundaryNavigation = (event) => {
    if (!(event.target instanceof Element)) return;

    const nextButton = event.target.closest('.slideshow-control--next');
    const previousButton = event.target.closest('.slideshow-control--previous');
    if (!nextButton && !previousButton) return;

    const slideshow = this.slideshow;
    if (!slideshow) return;

    const direction = nextButton ? 1 : -1;
    const slideCount = slideshow.slides?.length || 1;
    // `slideshow.current` can move ahead while the smooth scroll is still
    // settling. The rendered counter is the image the customer can actually
    // see, so use it for the colour boundary decision.
    const renderedPosition = Number.parseInt(this.querySelector('[ref="current"]')?.textContent || '', 10);
    const currentIndex = Number.isFinite(renderedPosition) ? renderedPosition - 1 : slideshow.current;
    const targetSlideIndex = currentIndex + direction;

    // Own arrow navigation with an instant single-step selection. Smooth-scroll
    // state can get one image ahead of the rendered counter and would otherwise
    // move to the next colour before the customer sees the final image.
    if (targetSlideIndex >= 0 && targetSlideIndex < slideCount) {
      event.preventDefault();
      event.stopImmediatePropagation();
      slideshow.select(targetSlideIndex, event, { animate: false });
      return;
    }

    const productSection = this.closest('.shopify-section');
    const picker = productSection?.querySelector('variant-picker');
    if (!picker) return;

    const colourFieldset = Array.from(picker.querySelectorAll('fieldset')).find((fieldset) => {
      const label = fieldset.querySelector('legend')?.textContent?.trim().toLowerCase() || '';
      return label.includes('color') || label.includes('colour') || label.includes('لون');
    });
    const inputs = Array.from(colourFieldset?.querySelectorAll('input[type="radio"]') || []);
    if (inputs.length < 2) return;

    const currentColourIndex = Math.max(0, inputs.findIndex((input) => input.checked));
    const targetIndex = (currentColourIndex + direction + inputs.length) % inputs.length;
    const targetInput = inputs[targetIndex];
    if (!(targetInput instanceof HTMLInputElement)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    productSection?.setAttribute('data-sportstuff-gallery-boundary', direction === -1 ? 'last' : 'first');
    targetInput.click();
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
